import React, { useRef, useState, useEffect } from "react";
import EditableField from "./EditableField";
import { updateUserById } from "../../services/userService";
import { useUser } from "../../contexts/UserContext";
import { TiUserOutline } from "react-icons/ti";
import { Utils } from "../../utilities/Utils";

interface ProfileSidebarProps {
  avatarUrl: string;
  name: string;
  bio: string;
  country: string;
  email: string;
  lastLoginDate?: string;
  onUpload: (file: File) => Promise<string>;
  loading: boolean;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({
  avatarUrl,
  name,
  bio,
  country,
  email,
  lastLoginDate,
  onUpload,
  loading,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user, updateUser } = useUser();
  const userId = user?.userId;

  const [profile, setProfile] = useState({ avatarUrl, name, bio, country });
  const [successMessage, setSuccessMessage] = useState("");

  const [newAvatarFile, setNewAvatarFile] = useState<File | null>(null);
  const [newAvatarPreview, setNewAvatarPreview] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    setProfile({ avatarUrl, name, bio, country });
  }, [avatarUrl, name, bio, country]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewAvatarFile(file);
      setNewAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleAvatarCancel = () => {
    setNewAvatarFile(null);
    setNewAvatarPreview(null);
  };

  const handleAvatarSave = async () => {
    if (newAvatarFile && userId) {
      try {
        setAvatarUploading(true);
        const newUrl = await onUpload(newAvatarFile);
        const updated = { ...profile, avatarUrl: newUrl };
        setProfile(updated);
        await updateUserById(userId, {
          avatar: newUrl,
          name: updated.name,
          country: updated.country,
          bio: updated.bio,
        });
        window.alert("Avatar updated successfully.");
        //setSuccessMessage("Avatar updated successfully.");
      } catch (err) {
        alert("Failed to upload avatar.");
        console.error(err);
      } finally {
        setAvatarUploading(false);
        setNewAvatarFile(null);
        setNewAvatarPreview(null);
      }
    }
  };

  const handlePartialSave = async (key: "name" | "bio" | "country", value: string) => {
    if (!userId) return;
    const updated = { ...profile, [key]: value };
    setProfile(updated);
    try {
      const updatedUser = await updateUserById(userId, { [key]: value });
      window.alert(`${key[0].toUpperCase() + key.slice(1)} updated successfully.`);
      //setSuccessMessage(`${key[0].toUpperCase() + key.slice(1)} updated successfully.`);
      updateUser(updatedUser);
    } catch (error) {
      alert("Failed to update profile.");
      console.error(error);
    }
  };

  return (
    <div className="relative mt-16 p-4">
      <h2 className="text-2xl font-bold text-white text-center mb-6">Welcome, {profile.name}</h2>

      <div className="text-center mb-6">
        {(newAvatarPreview || profile.avatarUrl) &&
          <img
            src={newAvatarPreview || profile.avatarUrl}
            alt="Avatar"
            className="w-28 h-28 mx-auto rounded-full object-cover border cursor-pointer hover:opacity-80 transition"
            onClick={() => fileInputRef.current?.click()}
          />}
        {(!newAvatarPreview && !profile.avatarUrl) &&
          <TiUserOutline className="w-28 h-28 text-white mx-auto rounded-full border cursor-pointer hover:opacity-80 transition"
            onClick={() => fileInputRef.current?.click()} />
        }
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleAvatarChange}
          className="hidden"
        />
        <p className="text-sm text-background mt-2">Click avatar to change</p>

        {newAvatarFile && (
          <div className="flex justify-center mt-2 gap-2">
            <button
              type="button"
              className="custom-btn min-w-[80px]"
              data-btn-type="negative"
              onClick={handleAvatarCancel}
              disabled={avatarUploading}
            >
              Cancel
            </button>

            <button
              type="button"
              className="custom-btn min-w-[80px]"
              onClick={handleAvatarSave}
              disabled={avatarUploading}
            >
              {avatarUploading ? "Saving..." : "Confirm"}
            </button>
          </div>
        )}
      </div>

      <label htmlFor="email" className="block text-background text-sm">Email</label>
      <EditableField
        id="email"
        value={email}
        editable={false}
      />

      <label htmlFor="name" className="block text-background text-sm">Name</label>
      <EditableField
        id="name"
        value={profile.name}
        onSave={(newName) => handlePartialSave("name", newName)}
      />

      <label htmlFor="country" className="block text-background text-sm">Country</label>
      <EditableField
        id="country"
        value={profile.country}
        placeholder="Somewhere wonderful"
        onSave={(newCountry) => handlePartialSave("country", newCountry)}
      />

      <label htmlFor="bio" className="block text-background text-sm">Bio</label>
      <EditableField
        id="bio"
        value={profile.bio}
        multiline
        placeholder="Share a bit about who you are"
        onSave={(newBio) => handlePartialSave("bio", newBio)}
      />

      <label htmlFor="last-login" className="block text-background text-sm">Last login</label>
      <EditableField
        id="last-login"
        value={lastLoginDate ? Utils.formatDate(lastLoginDate) : "First Login"}
        editable={false}
      />

      {successMessage && (
        <div className="text-background text-center my-4 relative p-8">
          <div className="bg-primary/30 h-full absolute top-0 left-0 w-full"></div>
          <span className="relative">{successMessage}</span>
        </div>
      )}
    </div>
  );
};

export default ProfileSidebar;
